/**
 * Credit packages and the free-amount top-up: price math, request parsing
 * and the Stripe Checkout parameters. Pure — no Stripe client, no DB — so the
 * checkout route, the webhook and the plans page share one definition and
 * node:test can cover it (lib/credit-packages.test.cjs).
 */
import type Stripe from 'stripe';
import {
  AUTO_TOPUP_DEFAULT_PACKAGE_ID,
  CREDIT_PACKAGES,
  CREDIT_TOPUP_CREDITS_PER_UNIT,
  CREDIT_TOPUP_DEFAULT_QUANTITY,
  CREDIT_TOPUP_MAX_QUANTITY,
  CREDIT_TOPUP_MIN_QUANTITY,
  CREDIT_TOPUP_UNIT_AMOUNT_CENTS,
  type CreditPackage,
  type CreditPackageId,
} from '@/config/stripe-plans.config';

/** Stripe's checkout-create parameters, taken from the method itself (the
 *  SDK's namespace paths moved between major versions). */
type SessionCreateParams = NonNullable<Parameters<Stripe['checkout']['sessions']['create']>[0]>;
type InvoiceCreation = NonNullable<SessionCreateParams['invoice_creation']>;

/** German VAT added on top of every net price. */
export const VAT_PERCENT = 19;

export function findCreditPackage(id: unknown): CreditPackage | null {
  return CREDIT_PACKAGES.find((pkg) => pkg.id === id) ?? null;
}

/** What the package's credits cost at the top-up list price (1 ct each). */
export function listPriceCents(credits: number): number {
  return Math.round((credits * CREDIT_TOPUP_UNIT_AMOUNT_CENTS) / CREDIT_TOPUP_CREDITS_PER_UNIT);
}

/** Whole-percent saving against the list price; 0 for the starter package. */
export function packageDiscountPercent(pkg: CreditPackage): number {
  const list = listPriceCents(pkg.credits);
  return list > 0 ? Math.round((1 - pkg.priceCents / list) * 100) : 0;
}

/** Net euro cents per credit, e.g. 0.6 for the professional package. */
export function centsPerCredit(pkg: CreditPackage): number {
  return pkg.priceCents / pkg.credits;
}

/** Gross amount (net + VAT) in cents, rounded like Stripe's tax line. */
export function grossCents(netCents: number): number {
  return Math.round((netCents * (100 + VAT_PERCENT)) / 100);
}

/** The largest package discount, for copy like "up to 40 % cheaper". */
export function maxPackageDiscountPercent(): number {
  return Math.max(0, ...CREDIT_PACKAGES.map(packageDiscountPercent));
}

/**
 * The cheapest purchase: the any-amount top-up at its minimum quantity
 * (CREDIT_TOPUP_MIN_QUANTITY units of CREDIT_TOPUP_UNIT_AMOUNT_CENTS, each
 * CREDIT_TOPUP_CREDITS_PER_UNIT credits) — the pricing page's "from" price.
 */
export function minimumTopup(): { priceCents: number; credits: number } {
  return {
    priceCents: CREDIT_TOPUP_MIN_QUANTITY * CREDIT_TOPUP_UNIT_AMOUNT_CENTS,
    credits: CREDIT_TOPUP_MIN_QUANTITY * CREDIT_TOPUP_CREDITS_PER_UNIT,
  };
}

export function smallestPackage(): CreditPackage {
  return CREDIT_PACKAGES.reduce((a, b) => (b.priceCents < a.priceCents ? b : a));
}

/**
 * The in-page checkout (our modal with Stripe Elements on a Checkout Session
 * instead of the hosted checkout.stripe.com redirect). Off unless the build sets
 * NEXT_PUBLIC_STRIPE_EMBEDDED_CHECKOUT=1 — prod (build.yml) and dev do. Written
 * as the literal `process.env.NEXT_PUBLIC_…` so Next inlines it into the
 * client and server bundles alike.
 */
export function embeddedCheckoutEnabled(): boolean {
  return embeddedFlagOn(process.env.NEXT_PUBLIC_STRIPE_EMBEDDED_CHECKOUT);
}

/** The flag's value test: only the exact string '1' switches it on. */
export function embeddedFlagOn(flag: string | undefined): boolean {
  return flag === '1';
}

/**
 * Auto top-up (the checkout modal's switch, the billing page's card, the
 * card-setup checkout and switching it on in the settings API). Off unless
 * the build sets NEXT_PUBLIC_AUTO_TOPUP=1 — only the dev build does; prod
 * has no off-session trigger wired yet (no AUTO_TOPUP_SIGNAL_SECRET, no
 * cron). The cron and internal routes are not gated. Literal
 * `process.env.NEXT_PUBLIC_…` so Next inlines it into both bundles.
 */
export function autoTopupEnabled(): boolean {
  return autoTopupFlagOn(process.env.NEXT_PUBLIC_AUTO_TOPUP);
}

/** The auto top-up flag's value test: only the exact string '1' is on. */
export function autoTopupFlagOn(flag: string | undefined): boolean {
  return flag === '1';
}

export type CheckoutRequest =
  | { kind: 'package'; pkg: CreditPackage; autoTopup: boolean; returnPath: string; embedded: boolean }
  | { kind: 'topup'; quantity: number; autoTopup: boolean; returnPath: string; embedded: boolean }
  | { kind: 'setup'; returnPath: string }
  | { kind: 'rejected'; status: number; code: string; error: string };

/** Only a same-site path ("/de/…", not "//evil.com" or a full URL). */
export function safeReturnPath(raw: unknown, fallback = '/plans'): string {
  return typeof raw === 'string' && /^\/(?![/\\])/.test(raw) ? raw.slice(0, 500) : fallback;
}

/** Clamp the top-up slider's quantity; a bogus value gets the default. */
export function clampTopupQuantity(raw: unknown): number {
  const n = Number(raw);
  if (raw === undefined || raw === null || !Number.isFinite(n)) return CREDIT_TOPUP_DEFAULT_QUANTITY;
  return Math.min(Math.max(Math.round(n), CREDIT_TOPUP_MIN_QUANTITY), CREDIT_TOPUP_MAX_QUANTITY);
}

/**
 * Classify a POST /api/stripe/create-checkout-session body. A `priceId`
 * (the old Pro subscription checkout) is rejected outright: Pro is no longer
 * sold, and a stale plans page or a hand-crafted request must not start a
 * subscription. Grandfathered subscribers manage theirs in the portal.
 *
 * With the auto top-up flag off, `autoTopup: true` is dropped (no card
 * saving, no auto top-up metadata) and the card-setup checkout is refused
 * with 409, so a stale client cannot switch auto top-up on.
 */
export function parseCheckoutRequest(
  body: unknown,
  /** process.env.NEXT_PUBLIC_AUTO_TOPUP, passed in so tests don't depend
   *  on the environment they run in. */
  autoTopupFlag: string | undefined,
): CheckoutRequest {
  const autoTopupOn = autoTopupFlagOn(autoTopupFlag);
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  if (b.priceId !== undefined && b.priceId !== null && b.priceId !== '') {
    return {
      kind: 'rejected',
      status: 410,
      code: 'subscriptions_discontinued',
      error: 'Subscriptions are no longer sold — buy a credit package instead.',
    };
  }
  if (b.packageId !== undefined) {
    const pkg = findCreditPackage(b.packageId);
    if (!pkg) {
      return { kind: 'rejected', status: 400, code: 'unknown_package', error: 'Unknown credit package' };
    }
    return {
      kind: 'package',
      pkg,
      autoTopup: autoTopupOn && b.autoTopup === true,
      returnPath: safeReturnPath(b.returnPath),
      embedded: b.embedded === true,
    };
  }
  if (b.topup === true) {
    return {
      kind: 'topup',
      quantity: clampTopupQuantity(b.quantity),
      autoTopup: autoTopupOn && b.autoTopup === true,
      returnPath: safeReturnPath(b.returnPath),
      embedded: b.embedded === true,
    };
  }
  if (b.setup === 'auto_topup') {
    if (!autoTopupOn) {
      return { kind: 'rejected', status: 409, code: 'auto_topup_unavailable', error: 'Auto top-up is not available' };
    }
    return { kind: 'setup', returnPath: safeReturnPath(b.returnPath, '/account/billing') };
  }
  return { kind: 'rejected', status: 400, code: 'invalid_request', error: 'packageId or topup is required' };
}

interface CheckoutContext {
  customerId: string;
  userSub: string;
  appUrl: string;
  vatTaxRateId: string;
  accountTaxIds: string[];
}

function invoiceCreation(accountTaxIds: string[]): InvoiceCreation {
  // German business customers expect a proper invoice for one-time payments
  // (subscriptions invoice automatically, payment mode doesn't).
  return {
    enabled: true,
    ...(accountTaxIds.length > 0 ? { invoice_data: { account_tax_ids: accountTaxIds } } : {}),
  };
}

/**
 * A package checkout. With `autoTopup`, the card is saved for off-session
 * use (`setup_future_usage`), and the webhook turns auto top-up on with this
 * package once the payment completes.
 */
export function packageCheckoutParams(
  ctx: CheckoutContext,
  pkg: CreditPackage,
  priceId: string,
  opts: { autoTopup: boolean; returnPath: string },
): SessionCreateParams {
  return {
    customer: ctx.customerId,
    client_reference_id: ctx.userSub,
    mode: 'payment',
    payment_method_types: ['card'],
    line_items: [{ price: priceId, quantity: 1, tax_rates: [ctx.vatTaxRateId] }],
    invoice_creation: invoiceCreation(ctx.accountTaxIds),
    ...(opts.autoTopup ? { payment_intent_data: { setup_future_usage: 'off_session' as const } } : {}),
    success_url: `${ctx.appUrl}/account/billing?topup=1&session_id={CHECKOUT_SESSION_ID}`,
    cancel_url: `${ctx.appUrl}${opts.returnPath}`,
    metadata: {
      userSub: ctx.userSub,
      type: 'credit_package',
      packageId: pkg.id,
      credits: String(pkg.credits),
      autoTopup: opts.autoTopup ? '1' : '0',
    },
  };
}

/**
 * The free-amount top-up: 1 € / 100 credits, quantity adjustable on Stripe.
 * With `autoTopup` (the checkout modal's switch on "Other") the card is saved
 * for off-session use and the webhook turns auto top-up on with the default
 * refill package (a custom amount is no package to buy again).
 */
export function topupCheckoutParams(
  ctx: CheckoutContext,
  priceId: string,
  opts: { quantity: number; returnPath: string; autoTopup?: boolean },
): SessionCreateParams {
  return {
    customer: ctx.customerId,
    client_reference_id: ctx.userSub,
    mode: 'payment',
    payment_method_types: ['card'],
    line_items: [
      {
        price: priceId,
        quantity: opts.quantity,
        adjustable_quantity: {
          enabled: true,
          minimum: CREDIT_TOPUP_MIN_QUANTITY,
          maximum: CREDIT_TOPUP_MAX_QUANTITY,
        },
        tax_rates: [ctx.vatTaxRateId],
      },
    ],
    invoice_creation: invoiceCreation(ctx.accountTaxIds),
    ...(opts.autoTopup ? { payment_intent_data: { setup_future_usage: 'off_session' as const } } : {}),
    // `topup=1` keeps the billing page's UpgradeFinalizer from treating this
    // as a subscription (no subscription to poll, no role refresh).
    success_url: `${ctx.appUrl}/account/billing?topup=1&session_id={CHECKOUT_SESSION_ID}`,
    cancel_url: `${ctx.appUrl}${opts.returnPath}`,
    metadata: {
      userSub: ctx.userSub,
      type: 'credit_topup',
      ...(opts.autoTopup ? { autoTopup: '1' } : {}),
    },
  };
}

/**
 * Save a card for auto top-up without buying anything (the billing page's
 * toggle when no card is on file). The webhook stores the payment method
 * and switches auto top-up on.
 */
export function autoTopupSetupParams(
  ctx: Omit<CheckoutContext, 'vatTaxRateId' | 'accountTaxIds'>,
  opts: { returnPath: string },
): SessionCreateParams {
  return {
    customer: ctx.customerId,
    client_reference_id: ctx.userSub,
    mode: 'setup',
    currency: 'eur',
    payment_method_types: ['card'],
    setup_intent_data: { metadata: { userSub: ctx.userSub, type: 'auto_topup_setup' } },
    success_url: `${ctx.appUrl}/account/billing?autotopup=1`,
    cancel_url: `${ctx.appUrl}${opts.returnPath}`,
    metadata: { userSub: ctx.userSub, type: 'auto_topup_setup' },
  };
}

/**
 * Turn a hosted package/top-up checkout into a custom-UI Checkout Session
 * (Stripe Elements in our modal): the same session (line items, VAT rate,
 * invoice, metadata, card saving), so the webhook fulfils it the same way.
 * On top of the hosted one it asks for the billing address (required, for
 * the invoice) and allows a business tax ID, both saved to the Stripe
 * customer (`customer_update`, which tax ID collection requires for an
 * existing customer) and so shown on the invoice. No success/cancel URL:
 * closing the modal is the cancel and card payments confirm in the page;
 * `return_url` is the hosted success URL, used only after a redirect.
 *
 * `'custom'` is the value for the pinned API version 2026-02-25.clover;
 * stripe-node 22's types already name it `'elements'` (2026-03-25.dahlia),
 * which this API version rejects, hence the cast.
 */
export function customCheckoutParams(params: SessionCreateParams): SessionCreateParams {
  const { success_url: successUrl, cancel_url: _cancelUrl, ...rest } = params;
  return {
    ...rest,
    ui_mode: 'custom' as unknown as SessionCreateParams['ui_mode'],
    return_url: successUrl,
    billing_address_collection: 'required',
    tax_id_collection: { enabled: true },
    customer_update: { name: 'auto', address: 'auto' },
  };
}

/**
 * The session parameters for a package or top-up request: hosted Checkout,
 * or the in-page custom-UI session when the request asks for it AND the
 * flag is on.
 * With the flag off an `embedded: true` request still gets the hosted
 * session, parameter for parameter (lib/credit-packages.test.cjs pins it).
 */
export function purchaseCheckoutParams(
  parsed: Extract<CheckoutRequest, { kind: 'package' | 'topup' }>,
  ctx: CheckoutContext,
  priceId: string,
  /** process.env.NEXT_PUBLIC_STRIPE_EMBEDDED_CHECKOUT, passed in so tests
   *  don't depend on the environment they run in. */
  flag: string | undefined,
): { params: SessionCreateParams; embedded: boolean } {
  const params =
    parsed.kind === 'package'
      ? packageCheckoutParams(ctx, parsed.pkg, priceId, { autoTopup: parsed.autoTopup, returnPath: parsed.returnPath })
      : topupCheckoutParams(ctx, priceId, {
          quantity: parsed.quantity,
          returnPath: parsed.returnPath,
          autoTopup: parsed.autoTopup,
        });
  const embedded = parsed.embedded && embeddedFlagOn(flag);
  return { params: embedded ? customCheckoutParams(params) : params, embedded };
}

type SessionUpdateParams = NonNullable<Parameters<Stripe['checkout']['sessions']['update']>[1]>;

/** The metadata keys a package or top-up session may carry beyond
 *  `userSub` and `type`; an update unsets the ones the new choice lacks. */
const PURCHASE_METADATA_KEYS = ['packageId', 'credits', 'autoTopup'] as const;

export type CheckoutUpdatePlan =
  | { ok: true; params: Pick<SessionUpdateParams, 'line_items' | 'metadata'> }
  | { ok: false; status: number; code: string };

/**
 * Switch an open in-page (custom-UI) session to another package or the
 * free-amount top-up, in place: the line items and metadata a fresh session
 * for that choice would get (same builders, so the webhook credits the final
 * selection exactly as if it had been chosen first), never prices from the
 * client. Card saving is fixed when the session is created, so the session's
 * own `autoTopup` flag carries over (packages and "Other" both support it).
 */
export function checkoutUpdatePlan(
  session: {
    client_reference_id: string | null;
    status: string | null;
    ui_mode: string | null;
    mode: string | null;
    metadata?: Record<string, string> | null;
  },
  userSub: string,
  parsed: CheckoutRequest,
  ctx: CheckoutContext,
  priceId: string,
): CheckoutUpdatePlan {
  if (session.client_reference_id !== userSub) return { ok: false, status: 404, code: 'not_found' };
  const type = session.metadata?.type;
  if (session.mode !== 'payment' || session.ui_mode !== 'custom' || (type !== 'credit_package' && type !== 'credit_topup')) {
    return { ok: false, status: 409, code: 'not_updatable' };
  }
  if (session.status !== 'open') return { ok: false, status: 409, code: 'session_closed' };
  if (parsed.kind !== 'package' && parsed.kind !== 'topup') return { ok: false, status: 400, code: 'invalid_request' };
  const autoTopup = session.metadata?.autoTopup === '1';
  const fresh =
    parsed.kind === 'package'
      ? packageCheckoutParams(ctx, parsed.pkg, priceId, { autoTopup, returnPath: '/plans' })
      : topupCheckoutParams(ctx, priceId, { quantity: parsed.quantity, returnPath: '/plans', autoTopup });
  const metadata: Record<string, string> = { ...(fresh.metadata as Record<string, string>) };
  for (const key of PURCHASE_METADATA_KEYS) if (!(key in metadata)) metadata[key] = '';
  // No line item ids: the old item is removed, the new one added.
  return { ok: true, params: { line_items: fresh.line_items as SessionUpdateParams['line_items'], metadata } };
}

/** Whole euros within the top-up bounds (the "Other" amount). */
export function isValidTopupQuantity(raw: unknown): boolean {
  return (
    typeof raw === 'number' &&
    Number.isInteger(raw) &&
    raw >= CREDIT_TOPUP_MIN_QUANTITY &&
    raw <= CREDIT_TOPUP_MAX_QUANTITY
  );
}

/** A purchase as the checkout modal is opened with (lib/stripe-client
 *  PurchaseRequestBody, restated here to keep lib/ pure of client code). */
export type CheckoutOpenBody =
  | { packageId: CreditPackageId; autoTopup?: boolean; returnPath?: string }
  | { topup: true; quantity?: number; autoTopup?: boolean; returnPath?: string };

/** The checkout modal's starting state: the picked card ('topup' is
 *  "Other"), the auto top-up switch (off unless the caller asked and the
 *  flag is on) and the "Other" amount. */
export function initialCheckoutState(
  opened: CheckoutOpenBody,
  autoTopupOn: boolean,
): { choice: CreditPackageId | 'topup'; autoTopup: boolean; topupEuros: number } {
  return {
    choice: 'packageId' in opened ? opened.packageId : 'topup',
    autoTopup: autoTopupOn && opened.autoTopup === true,
    topupEuros: 'topup' in opened ? clampTopupQuantity(opened.quantity) : CREDIT_TOPUP_DEFAULT_QUANTITY,
  };
}

/** The create-checkout-session body for what the modal shows. */
export function checkoutSessionBody(
  choice: CreditPackageId | 'topup',
  autoTopup: boolean,
  topupEuros: number,
  returnPath: string | undefined,
): CheckoutOpenBody {
  return choice === 'topup'
    ? { topup: true, quantity: topupEuros, ...(autoTopup ? { autoTopup } : {}), returnPath }
    : { packageId: choice, ...(autoTopup ? { autoTopup } : {}), returnPath };
}

/** The modal's first session, which CheckoutProvider asks for on open,
 *  before the modal's code and Stripe.js have loaded. The same body the
 *  modal would send for its starting state. */
export function firstCheckoutSessionBody(opened: CheckoutOpenBody, autoTopupOn: boolean): CheckoutOpenBody {
  const start = initialCheckoutState(opened, autoTopupOn);
  return checkoutSessionBody(start.choice, start.autoTopup, start.topupEuros, opened.returnPath);
}

/** What a completed payment-mode checkout grants, or null if it's not ours. */
export interface PurchaseGrant {
  kind: 'package' | 'topup';
  credits: number;
  /** Ledger idempotency key: the checkout session id. */
  periodKey: string;
  packageId: CreditPackageId | null;
  autoTopup: boolean;
  /** The package auto top-up buys from now on, when `autoTopup`: the bought
   *  package, or the default refill package for a free-amount top-up. */
  autoTopupPackageId: CreditPackageId | null;
}

/**
 * Resolve a `checkout.session.completed` payload to the credits it buys.
 * Package credits come from our config by `packageId`, never from the
 * metadata's `credits` (display only); top-up credits from the paid
 * quantity. Unpaid sessions grant nothing.
 */
export function purchaseGrantFromSession(
  session: {
    id: string;
    mode: string | null;
    payment_status: string | null;
    metadata?: Record<string, string> | null;
  },
  paidUnits: number,
): PurchaseGrant | null {
  if (session.mode !== 'payment' || session.payment_status !== 'paid') return null;
  const type = session.metadata?.type;
  if (type === 'credit_package') {
    const pkg = findCreditPackage(session.metadata?.packageId);
    if (!pkg) return null;
    const autoTopup = session.metadata?.autoTopup === '1';
    return {
      kind: 'package',
      credits: pkg.credits,
      periodKey: session.id,
      packageId: pkg.id,
      autoTopup,
      autoTopupPackageId: autoTopup ? pkg.id : null,
    };
  }
  if (type === 'credit_topup') {
    const credits = paidUnits * CREDIT_TOPUP_CREDITS_PER_UNIT;
    if (credits <= 0) return null;
    const autoTopup = session.metadata?.autoTopup === '1';
    return {
      kind: 'topup',
      credits,
      periodKey: session.id,
      packageId: null,
      autoTopup,
      autoTopupPackageId: autoTopup ? AUTO_TOPUP_DEFAULT_PACKAGE_ID : null,
    };
  }
  return null;
}
